import React from "react";
import { useTranslation } from "react-i18next";
import { Download } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import Modal from "@/components/controls/Modal";
import { formatGeneratedAtDate } from "@/utils/datetime";
import { cn } from "@/utils/utils";

/** What a report adds to the header of its expanded view. */
export interface DreamerModalHeaderSlots {
  /** Stands in for the title text, e.g. while the report is being renamed. */
  titleContent?: React.ReactNode;
  /** Actions before Download, and after it. */
  leadingActions?: React.ReactNode;
  trailingActions?: React.ReactNode;
}

interface DreamerAttachmentModalShellProps extends DreamerModalHeaderSlots {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  onDownload?: () => void;
  isDownloading?: boolean;
  canDownload?: boolean;
  generatedAt?: string;
  /** Stands in for the "Generated on" line, e.g. while the report is generating. */
  footer?: JSX.Element;
  contentPaddingClass?: string;
  children: JSX.Element;
}

const DreamerAttachmentModalShell: React.FC<
  DreamerAttachmentModalShellProps
> = ({
  isOpen,
  setIsOpen,
  title,
  titleContent,
  leadingActions,
  trailingActions,
  onDownload,
  isDownloading = false,
  canDownload = true,
  generatedAt,
  footer,
  contentPaddingClass = "p-6",
  children
}) => {
  const { t } = useTranslation();
  const generatedAtDate = formatGeneratedAtDate(generatedAt);
  const generatedAtLabel = generatedAtDate
    ? t("chat.generatedOn", { date: generatedAtDate })
    : null;

  const modalTitle = (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-start w-full gap-2">
        {titleContent ?? (
          <Text as="h1" variant="lgSubtitleH1">
            {title}
          </Text>
        )}
        {/* The header sits in the dialog's title, whose heading type the
            actions must not inherit. */}
        <div className="flex items-center gap-1 mr-2 text-base font-medium">
          {leadingActions}
          <IconButton
            size="lg"
            onClick={onDownload}
            disabled={!onDownload || !canDownload || isDownloading}
          >
            <Download />
          </IconButton>
          {trailingActions}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      title={modalTitle}
      content={children}
      footer={
        footer ??
        (generatedAtLabel ? (
          <Text variant="bodySecondary">{generatedAtLabel}</Text>
        ) : undefined)
      }
      headerClassname="border-0"
      footerClassname="pl-5"
      contentClassname="w-full flex flex-col gap-0 max-w-[88vw] h-[82vh]"
      hideDivider={true}
      showCloseButton={true}
      contentContainerClassname={cn(
        "flex-1 min-h-0 flex flex-col",
        contentPaddingClass
      )}
      // Radix focuses the first focusable child on open, which is now a
      // header action; the window itself takes the focus instead.
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        const target = event.currentTarget as HTMLElement | null;
        requestAnimationFrame(() => target?.focus());
      }}
    />
  );
};

export default DreamerAttachmentModalShell;
